import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CTAButton } from "./CTAButton";
import { submitLead } from "../lib/submit";

const inputCls =
  "w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-signal transition-colors";

export function PartnerForm() {
  const { t } = useTranslation();
  const types = t("partners.types", { returnObjects: true });
  const products = t("partners.products", { returnObjects: true });
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ company: "", contact: "", email: "", whatsapp: "", website: "", country: "", partnerType: "", volume: "", markets: "", products: [], notes: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleProduct = (p) => setF((s) => ({ ...s, products: s.products.includes(p) ? s.products.filter((x) => x !== p) : [...s.products, p] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company || !f.contact) return toast.error(t("partners.errRequired"));
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error(t("partners.errEmail"));
    if (!f.partnerType) return toast.error(t("partners.errType"));
    const payload = {
      source: "r0am_website", brand: "R0AM by Wadiah Mutual", lead_type: "partner_request",
      company_name: f.company, contact_person: f.contact, email: f.email, whatsapp: f.whatsapp,
      website: f.website, country: f.country, partner_type: f.partnerType,
      estimated_monthly_volume: f.volume, markets_served: f.markets, interested_products: f.products, notes: f.notes,
    };
    const res = await submitLead("partners", payload);
    if (!res.ok) return toast.error(t("partners.errRequired"));
    setDone(true);
    toast.success(t("partners.successToast"));
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="surface p-10 text-center" data-testid="partner-success">
        <div className="w-14 h-14 rounded-full bg-health/15 grid place-items-center mx-auto"><CheckCircle2 size={28} className="text-health" /></div>
        <h3 className="mt-5 font-display font-bold text-2xl">{t("partners.successTitle", { name: f.contact.split(" ")[0] })}</h3>
        <p className="mt-2 text-white/60">{t("partners.successBody", { company: f.company })}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="surface p-6 sm:p-9" data-testid="partner-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("partners.fCompany")}><input className={inputCls} value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="p-company" /></Field>
        <Field label={t("partners.fContact")}><input className={inputCls} value={f.contact} onChange={(e) => set("contact", e.target.value)} data-testid="p-contact" /></Field>
        <Field label={t("partners.fEmail")}><input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="p-email" /></Field>
        <Field label={t("partners.fWhatsapp")}><input className={inputCls} value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+..." data-testid="p-whatsapp" /></Field>
        <Field label={t("partners.fWebsite")}><input className={inputCls} value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="p-website" /></Field>
        <Field label={t("partners.fCountry")}><input className={inputCls} value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="p-country" /></Field>
        <Field label={t("partners.fType")}>
          <select className={inputCls} value={f.partnerType} onChange={(e) => set("partnerType", e.target.value)} data-testid="p-type">
            <option value="">{t("partners.fSelect")}</option>
            {types.map((tp) => <option key={tp} value={tp} className="bg-navy-900">{tp}</option>)}
          </select>
        </Field>
        <Field label={t("partners.fVolume")}><input className={inputCls} value={f.volume} onChange={(e) => set("volume", e.target.value)} placeholder={t("partners.fVolumePh")} data-testid="p-volume" /></Field>
      </div>
      <Field label={t("partners.fMarkets")} className="mt-4"><input className={inputCls} value={f.markets} onChange={(e) => set("markets", e.target.value)} placeholder={t("partners.fMarketsPh")} data-testid="p-markets" /></Field>

      <Field label={t("partners.fProducts")} className="mt-4">
        <div className="flex flex-wrap gap-2">
          {products.map((p) => (
            <button type="button" key={p} onClick={() => toggleProduct(p)} data-testid={`p-product-${p}`} className={`px-3 py-2 rounded-md text-sm border transition-all ${f.products.includes(p) ? "bg-navy-700 border-signal text-white" : "bg-white/[0.02] border-white/10 text-white/65 hover:border-white/25"}`}>{p}</button>
          ))}
        </div>
      </Field>

      <Field label={t("partners.fNotes")} className="mt-4"><textarea rows="3" className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="p-notes" /></Field>

      <div className="mt-6"><CTAButton type="submit" variant="primary" fullWidth testId="partner-submit">{t("partners.submit")} <ArrowRight size={16} className="rtl:rotate-180" /></CTAButton></div>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block overline text-white/40 mb-2">{label}</label>
      {children}
    </div>
  );
}

export default PartnerForm;
